import { useEffect } from "react";
import type { Activity, CreditState, Profile, Shipment, Subscription, UsageSeries } from "api";
import cn from "classnames";
import { creditKpi } from "components/Billing/credits.model";
import type { LoadStatus } from "hooks/useProfile";
import { ActivityCard } from "./ActivityCard";
import { ArrivalsCard } from "./ArrivalsCard";
import { AttentionCard } from "./AttentionCard";
import { CallsCard } from "./CallsCard";
import { CreditsUsageCard } from "./CreditsUsageCard";
import type { GotoTarget } from "./dashboard.model";
import { greeting, kpis, summaryLine } from "./dashboard.model";
import { ProductsCard } from "./ProductsCard";
import { UsageCard } from "./UsageCard";

type DashboardPaneProps = {
  profile: Profile | null;
  shipments: Shipment[];
  shipmentsStatus: LoadStatus;
  subscriptions: Subscription[];
  subscriptionsStatus: LoadStatus;
  usage: UsageSeries | null;
  usageStatus: LoadStatus;
  activity: Activity[];
  activityStatus: LoadStatus;
  /** The QCredits balance; null until it arrives. */
  credits: CreditState | null;
  onGoto: (target: GotoTarget) => void;
  onSessionLost: () => void;
  /** Called once each time the pane is opened, to refresh what it summarises. */
  onOpened: () => void;
};

/**
 * The account at a glance: what is late, what arrives next, what the
 * subscriptions are being spent on, and what happened lately.
 *
 * Every card reads data the cabinet already holds or fetches for itself, and
 * every figure links to the pane it came from — the dashboard summarises the
 * cabinet, it does not duplicate it.
 */
export const DashboardPane = ({
  profile,
  shipments,
  shipmentsStatus,
  subscriptions,
  subscriptionsStatus,
  usage,
  usageStatus,
  activity,
  activityStatus,
  credits,
  onGoto,
  onSessionLost,
  onOpened,
}: DashboardPaneProps) => {
  // The pane is unmounted while another one is open, so mounting is exactly the
  // moment it was opened — and the moment its numbers may have moved on.
  useEffect(() => onOpened(), [onOpened]);

  const loaded = shipmentsStatus === "ready" && subscriptionsStatus === "ready";

  return (
    <div className="pc-pane">
      <div className="dsh__hd">
        <div>
          <div className="dsh__hi">{greeting(profile)}</div>
          <p className="dsh__sub">{loaded ? summaryLine(shipments, subscriptions) : "Loading your account…"}</p>
        </div>

        <div className="dsh__hd-a">
          <button type="button" className="pcbtn" onClick={() => onGoto({ pane: "myqubics" })}>
            Subscriptions
          </button>
          <button
            type="button"
            className="pcbtn pcbtn--main"
            onClick={() => onGoto({ pane: "shipments", sub: "shipments" })}
          >
            Add shipment
          </button>
        </div>
      </div>

      <div className="dsh__kpi">
        {kpis(shipments, subscriptions).map((kpi) => (
          <button key={kpi.key} type="button" className="dsh__k" onClick={() => onGoto(kpi.goto)}>
            <span className="dsh__k-l">{kpi.label}</span>
            <b className="dsh__k-v">
              {loaded ? kpi.value : "—"}
              {loaded && kpi.suffix !== undefined && <i>{kpi.suffix}</i>}
            </b>
            <em
              className={cn("dsh__k-d", {
                "dsh__k-d--ok": loaded && kpi.hintTone === "ok",
                "dsh__k-d--bad": loaded && kpi.hintTone === "bad",
              })}
            >
              {loaded ? kpi.hint : "…"}
            </em>
          </button>
        ))}

        {credits !== null && <CreditKpi credits={credits} onGoto={onGoto} />}
      </div>

      <div className="dsh__grid">
        <AttentionCard
          shipments={shipments}
          shipmentsStatus={shipmentsStatus}
          subscriptions={subscriptions}
          subscriptionsStatus={subscriptionsStatus}
          credits={credits}
          onGoto={onGoto}
        />

        <UsageCard subscriptions={subscriptions} status={subscriptionsStatus} onGoto={onGoto} />

        <CreditsUsageCard credits={credits} onGoto={onGoto} onSessionLost={onSessionLost} />

        <ArrivalsCard shipments={shipments} status={shipmentsStatus} onGoto={onGoto} />

        <CallsCard usage={usage} status={usageStatus} />

        <ProductsCard subscriptions={subscriptions} status={subscriptionsStatus} onGoto={onGoto} />

        <ActivityCard activity={activity} status={activityStatus} />
      </div>
    </div>
  );
};

/** "QCredits balance": the design's fourth KPI, linking to Plan & Billing. */
const CreditKpi = ({ credits, onGoto }: { credits: CreditState; onGoto: (target: GotoTarget) => void }) => {
  const kpi = creditKpi(credits);

  return (
    <button type="button" className="dsh__k" onClick={() => onGoto({ pane: "billing" })}>
      <span className="dsh__k-l">QCredits balance</span>
      <b className="dsh__k-v">{kpi.value}</b>
      <em className={cn("dsh__k-d", { "dsh__k-d--bad": kpi.tone === "bad" })}>{kpi.hint}</em>
    </button>
  );
};
